/**
 * Effective project labour roster — combines the formal
 * `projectLabour` collection with any labour derived from
 * historical `attendance` docs that pre-date the roster system.
 *
 * Web SDK port of mobile's
 *   /Users/gudupuramesh/Documents/siteexpens/src/features/attendance/useEffectiveProjectLabour.ts
 *
 * Behaviour:
 *   - Subscribes to `projectLabour where orgId == X AND projectId == Y`
 *     (source of truth, supports soft-disable + edit).
 *   - Subscribes to `attendance where orgId == X AND projectId == Y`
 *     to surface any labourer who exists in attendance but not yet
 *     in the roster (silent backfill — converges the data model).
 *   - Each derived-only labourer is upserted to `projectLabour` once
 *     per session (idempotent).
 *   - Final list = roster ∪ derived, deduped by labourId, excludes
 *     disabled, sorted by name.
 */
import {
  collection,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  upsertProjectLabour,
  type AttendanceRecord,
  type ProjectLabour,
} from '@/lib/attendance';
import { db } from '@/lib/firebase';

type DerivedLabour = Pick<
  ProjectLabour,
  'orgId' | 'projectId' | 'labourId' | 'labourName' | 'labourRole'
> & {
  description?: string;
  payRate?: number;
  payUnit?: 'day' | 'hour';
  createdBy: string;
  latestDate: string;
};

export type UseEffectiveProjectLabourResult = {
  data: ProjectLabour[];
  loading: boolean;
};

export function useEffectiveProjectLabour(
  orgId: string | null | undefined,
  projectId: string | undefined,
): UseEffectiveProjectLabourResult {
  const [roster, setRoster] = useState<ProjectLabour[]>([]);
  const [derived, setDerived] = useState<Map<string, DerivedLabour>>(
    () => new Map(),
  );
  const [rosterLoading, setRosterLoading] = useState(true);
  const [derivedLoading, setDerivedLoading] = useState(true);
  const backfilledRef = useRef<Set<string>>(new Set());

  // ── Roster subscription ────────────────────────────────────
  useEffect(() => {
    if (!orgId || !projectId) {
      setRoster([]);
      setRosterLoading(false);
      return;
    }
    setRosterLoading(true);
    return onSnapshot(
      query(
        collection(db, 'projectLabour'),
        where('orgId', '==', orgId),
        where('projectId', '==', projectId),
      ),
      (snap) => {
        setRoster(
          snap.docs.map((d) => ({
            id: d.id,
            ...(d.data() as Omit<ProjectLabour, 'id'>),
          })),
        );
        setRosterLoading(false);
      },
      (err) => {
        console.warn('[useEffectiveProjectLabour] roster error:', err);
        setRosterLoading(false);
      },
    );
  }, [orgId, projectId]);

  // ── Derived (from attendance) subscription ─────────────────
  useEffect(() => {
    if (!orgId || !projectId) {
      setDerived(new Map());
      setDerivedLoading(false);
      return;
    }
    setDerivedLoading(true);
    return onSnapshot(
      query(
        collection(db, 'attendance'),
        where('orgId', '==', orgId),
        where('projectId', '==', projectId),
      ),
      (snap) => {
        const map = new Map<string, DerivedLabour>();
        for (const d of snap.docs) {
          const r = d.data() as Omit<AttendanceRecord, 'id'>;
          const labourId = r.labourId;
          if (!labourId) continue;
          const prev = map.get(labourId);
          // Keep the most-recent attendance record's snapshot of the
          // labour's name / role / pay — those fields denormalise into
          // the roster doc on backfill.
          if (!prev || (r.date && r.date > prev.latestDate)) {
            map.set(labourId, {
              orgId: r.orgId,
              projectId: r.projectId,
              labourId,
              labourName: r.labourName,
              labourRole: r.labourRole,
              description: r.description,
              payRate: r.payRate,
              payUnit: r.payUnit,
              createdBy: r.createdBy,
              latestDate: r.date ?? '',
            });
          }
        }
        setDerived(map);
        setDerivedLoading(false);
      },
      (err) => {
        console.warn('[useEffectiveProjectLabour] derived error:', err);
        setDerivedLoading(false);
      },
    );
  }, [orgId, projectId]);

  // ── Silent backfill ───────────────────────────────────────
  // Any derived labour that doesn't yet have a roster doc → upsert
  // once. We track per-session via a ref so the same labourId isn't
  // re-written on every snapshot tick.
  useEffect(() => {
    if (!projectId || derivedLoading || rosterLoading) return;
    const rosterIds = new Set(roster.map((r) => r.labourId));
    derived.forEach((d) => {
      if (rosterIds.has(d.labourId)) return;
      if (backfilledRef.current.has(d.labourId)) return;
      backfilledRef.current.add(d.labourId);
      upsertProjectLabour({
        orgId: d.orgId,
        projectId: d.projectId,
        labourId: d.labourId,
        labourName: d.labourName,
        labourRole: d.labourRole,
        description: d.description,
        payRate: d.payRate,
        payUnit: d.payUnit,
        createdBy: d.createdBy,
        disabled: false,
      }).catch((err) => {
        console.warn('[useEffectiveProjectLabour] backfill error:', err);
        // Let it retry on the next snapshot.
        backfilledRef.current.delete(d.labourId);
      });
    });
  }, [derived, derivedLoading, projectId, roster, rosterLoading]);

  const data = useMemo<ProjectLabour[]>(() => {
    const byId = new Map<string, ProjectLabour>();
    for (const r of roster) byId.set(r.labourId, r);
    derived.forEach((d) => {
      if (byId.has(d.labourId)) return;
      byId.set(d.labourId, {
        id: `${d.projectId}_${d.labourId}`,
        orgId: d.orgId,
        projectId: d.projectId,
        labourId: d.labourId,
        labourName: d.labourName,
        labourRole: d.labourRole,
        description: d.description,
        payRate: d.payRate,
        payUnit: d.payUnit,
        disabled: false,
        createdBy: d.createdBy,
        createdAt: null,
      });
    });
    const merged = Array.from(byId.values()).filter((row) => !row.disabled);
    merged.sort((a, b) => a.labourName.localeCompare(b.labourName));
    return merged;
  }, [roster, derived]);

  return { data, loading: rosterLoading || derivedLoading };
}
